import { StandardEvents } from '@shopify/events';

/**
 * Amaya UI layer: small touches that make the shop feel alive.
 * - reveal on scroll (sections and cards fade up once, staggered)
 * - image skeletons: shimmer until the photo has loaded
 * - card -> product page image transition (Horizon's view transitions)
 * - bag badge bump when something is added
 * - thin progress bar while the next page loads
 * All of it is skipped in the theme editor and for reduced-motion users.
 */
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const designMode = document.documentElement.classList.contains('shopify-design-mode');

/* ---------- reveal on scroll ---------- */
function setupReveal(root = document) {
  if (reduced || designMode || !('IntersectionObserver' in window)) return;
  const targets = root.querySelectorAll(
    '.amaya-section__inner > *:not(.amaya-reveal), .amaya-drop__grid > .amaya-card:not(.amaya-reveal), .amaya-collection__grid > .amaya-card:not(.amaya-reveal), .amaya-occasions__row > .amaya-tile:not(.amaya-reveal), .amaya-steps__item:not(.amaya-reveal)'
  );
  if (!targets.length) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      io.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px 12% 0px', threshold: 0.01 });
  targets.forEach((el, i) => {
    const rect = el.getBoundingClientRect();
    // Anything already on screen at load shows immediately: no flash, no LCP penalty.
    if (rect.top < window.innerHeight * 0.9) return;
    el.classList.add('amaya-reveal');
    const siblings = Array.from(el.parentElement?.children || []);
    const idx = siblings.indexOf(el);
    el.style.setProperty('--amaya-delay', `${Math.min(idx, 5) * 45}ms`);
    io.observe(el);
  });
}

/* ---------- image skeletons ---------- */
function markLoaded(img) {
  const holder = img.closest('.amaya-card__media, .amaya-tile__media, .amaya-story__media, .amaya-hero__media');
  if (holder) holder.classList.add('is-loaded');
}
function setupSkeletons(root = document) {
  root.querySelectorAll('.amaya-card__media img, .amaya-tile__media img, .amaya-story__media img, .amaya-hero__media img').forEach((img) => {
    if (img.complete && img.naturalWidth > 0) markLoaded(img);
    else {
      img.addEventListener('load', () => markLoaded(img), { once: true });
      img.addEventListener('error', () => markLoaded(img), { once: true });
    }
  });
}

/* ---------- card -> product transition ---------- */
function setupCardTransitions() {
  if (!document.documentElement.dataset.amayaTransitions) return;
  document.addEventListener('click', (e) => {
    const link = e.target instanceof Element ? e.target.closest('.amaya-card__link, .amaya-card__title-link') : null;
    if (!link) return;
    const media = link.closest('.amaya-card')?.querySelector('.amaya-card__media');
    if (!media) return;
    media.setAttribute('data-view-transition-type', 'product-image-transition');
    media.setAttribute('data-view-transition-triggered', 'true');
  });
}

/* ---------- bag badge bump ---------- */
function setupBadgeBump() {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise?.then(() => {
      document.querySelectorAll('.cart-bubble').forEach((b) => {
        b.classList.remove('amaya-bump');
        void b.offsetWidth;
        b.classList.add('amaya-bump');
      });
    }).catch(() => {});
  });
}

/* ---------- page loader: a small sun rising over a wave ----------
   Shows only when the next page takes longer than 300 ms (CSS delay), so fast pages never flash it. */
function setupProgress() {
  if (reduced || designMode || document.documentElement.dataset.amayaLoader === 'off') return;
  const el = document.createElement('div');
  el.className = 'amaya-loader';
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = '<svg class="amaya-loader__svg" viewBox="0 0 120 64" width="120" height="64">' +
    '<defs><clipPath id="amaya-loader-clip"><rect x="0" y="0" width="120" height="64"/></clipPath></defs>' +
    '<g clip-path="url(#amaya-loader-clip)">' +
    '<g class="amaya-loader__sun"><g class="amaya-loader__rays">' +
    [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((a) => '<line x1="60" y1="20" x2="60" y2="14" transform="rotate(' + a + ' 60 30)"/>').join('') +
    '</g><circle cx="60" cy="30" r="7"/></g>' +
    '<path class="amaya-loader__wave" d="M-120 44 q15 -7 30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 V64 H-120 Z"/>' +
    '<path class="amaya-loader__wave amaya-loader__wave--back" d="M-120 48 q15 -6 30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 V64 H-120 Z"/>' +
    '</g></svg>';
  document.body.appendChild(el);
  let timer;
  const start = () => { clearTimeout(timer); el.classList.add('is-active'); timer = setTimeout(() => el.classList.remove('is-active'), 9000); };
  const stop = () => { clearTimeout(timer); el.classList.remove('is-active'); };
  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!a || a.target === '_blank' || a.hasAttribute('download') || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.defaultPrevented) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin || url.href.split('#')[0] === location.href.split('#')[0] || /^(mailto|tel|sms):/.test(a.href)) return;
    start();
  });
  window.addEventListener('pageshow', stop);
  window.addEventListener('pagehide', stop);
}

/* ---------- "Read more" on long intros ---------- */
let readMoreCount = 0;
function measureReadMore(el, btn) {
  if (el.dataset.moreOpen) return;
  // Hidden (display: none) or not laid out yet: leave it open, measure again later.
  if (!el.getClientRects().length) { el.classList.remove('is-clamped'); btn.hidden = true; return; }
  el.classList.add('is-clamped');
  const over = el.scrollHeight > el.clientHeight + 4;
  el.classList.toggle('is-clamped', over);
  btn.hidden = !over;
}
function setupReadMore() {
  document.querySelectorAll('[data-amaya-more]:not([data-more-ready])').forEach((el) => {
    el.dataset.moreReady = '1';
    if (!el.id) el.id = 'amaya-more-' + ++readMoreCount;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'amaya-more__btn';
    btn.textContent = 'Read more';
    btn.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', el.id);
    btn.addEventListener('click', () => {
      const open = el.classList.toggle('is-clamped') === false;
      if (open) el.dataset.moreOpen = '1'; else delete el.dataset.moreOpen;
      btn.textContent = open ? 'Read less' : 'Read more';
      btn.setAttribute('aria-expanded', String(open));
    });
    el.after(btn);
    const measure = () => measureReadMore(el, btn);
    measure();
    // Web fonts arriving late or a rotated phone change the line count: measure again.
    document.fonts?.ready.then(measure);
    let raf = 0;
    window.addEventListener('resize', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); });
  });
}

/* ---------- email forms without a page reload ----------
   Newsletter, signup card, register, quiz and back-in-stock post in the background. The page Shopify sends back
   carries the same form (same id) in its "thank you" or error state; that version replaces the form in place.
   If Shopify asks for a captcha, the form falls back to a normal submit. */
function setupAjaxForms() {
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.id || form.hasAttribute('data-no-ajax')) return;
    const type = form.querySelector('input[name="form_type"]');
    if (!type || type.value !== 'customer') return;
    e.preventDefault();
    // One request at a time (Horizon's signup button has no type attribute, so it cannot be relied on to be disabled).
    if (form.dataset.amayaBusy) return;
    form.dataset.amayaBusy = '1';
    let done = false;
    const btn = form.querySelector('button:not([type]), [type="submit"]');
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
    try {
      const res = await fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' });
      if (res.url.includes('/challenge')) { form.setAttribute('data-no-ajax', ''); form.submit(); return; }
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById(form.id);
      if (fresh) {
        form.replaceWith(fresh);
        const msg = fresh.querySelector('[class*="success"], [class*="error"], .form__message, [role="status"]') || fresh;
        msg.setAttribute('tabindex', '-1');
        msg.focus({ preventScroll: true });
      } else {
        const ok = res.ok && /customer_posted=true/.test(res.url);
        const note = document.createElement('p');
        note.className = 'amaya-form-note' + (ok ? ' is-ok' : ' is-error');
        note.setAttribute('role', 'status');
        note.textContent = ok ? (form.dataset.success || 'You are in. You will hear from us first.') : 'That did not work. Check your email address and try again.';
        form.querySelector('.amaya-form-note')?.remove();
        form.appendChild(note);
        if (ok) { done = true; form.querySelectorAll('input[type="email"], button:not([type]), [type="submit"]').forEach((el) => { el.disabled = true; }); }
      }
    } catch (err) {
      form.setAttribute('data-no-ajax', '');
      form.submit();
    } finally {
      delete form.dataset.amayaBusy;
      if (btn && btn.isConnected) { btn.removeAttribute('aria-busy'); if (!done) btn.disabled = false; }
    }
  });
}

/* ---------- generic dialogs (size guide etc.) ---------- */
function setupDialogs() {
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element)) return;
    const opener = e.target.closest('[data-amaya-dialog-open]');
    if (opener) {
      const dlg = document.getElementById(opener.getAttribute('data-amaya-dialog-open'));
      if (dlg && typeof dlg.showModal === 'function' && !dlg.open) dlg.showModal();
      return;
    }
    const sizeLink = e.target.closest('[data-amaya-open-sizeguide]');
    if (sizeLink) {
      const scope = sizeLink.closest('.shopify-section') || document;
      const dlg = scope.querySelector('.amaya-sizeguide dialog') || document.querySelector('.amaya-sizeguide dialog');
      if (dlg && typeof dlg.showModal === 'function' && !dlg.open) dlg.showModal();
      else if (!dlg) window.location.href = (window.Shopify?.routes?.root || '/') + 'pages/size-guide';
      return;
    }
    const closer = e.target.closest('[data-amaya-dialog-close]');
    if (closer) { closer.closest('dialog')?.close(); return; }
    if (e.target instanceof HTMLDialogElement && e.target.classList.contains('amaya-dialog')) e.target.close();
  });
}

// Pre-order: when the delivery block says the selected size is a pre-order, the main button reads "Pre-order".
function setupPreorderLabel() {
  const apply = () => {
    document.querySelectorAll('product-form-component button[name="add"]').forEach((btn) => {
      const scope = btn.closest('.shopify-section, dialog') || document;
      const preorder = !!scope.querySelector('.amaya-delivery--preorder');
      const textNode = Array.from(btn.childNodes).find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
      if (!textNode) return;
      if (preorder && !btn.dataset.amayaOriginal) {
        btn.dataset.amayaOriginal = textNode.textContent;
        textNode.textContent = textNode.textContent.replace(/\S.*\S/, 'Pre-order');
      } else if (!preorder && btn.dataset.amayaOriginal) {
        textNode.textContent = btn.dataset.amayaOriginal;
        delete btn.dataset.amayaOriginal;
      }
    });
  };
  apply();
  document.addEventListener('amaya:rerender', apply);
}

function init() {
  setupPreorderLabel();
  setupDialogs();
  setupAjaxForms();
  setupReadMore();
  setupSkeletons();
  setupReveal();
  setupCardTransitions();
  setupBadgeBump();
  setupProgress();
  // Section rendering / editor re-renders: re-run on new nodes.
  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      m.addedNodes.forEach((n) => {
        if (!(n instanceof Element)) return;
        setupSkeletons(n);
        if (n.querySelector('[data-amaya-more]') || n.matches('[data-amaya-more]')) setupReadMore();
        if (n.matches('.shopify-section, .amaya-section')) setupReveal(n);
      });
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

// Hero video: pause when off screen or when the visitor saves data.
(() => {
  const box = document.querySelector('[data-amaya-hero-video]');
  const v = box && box.querySelector('video');
  if (!v) return;
  // Data saver, or hidden by CSS (phones unless switched on, reduced motion): remove it so it stops downloading.
  if ((navigator.connection && navigator.connection.saveData) || getComputedStyle(box).display === 'none') { box.remove(); return; }
  v.muted = true;
  const play = () => v.play().catch(() => {});
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) => entries.forEach((e) => (e.isIntersecting ? play() : v.pause()))).observe(v);
  } else play();
})();
